import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { CreateQcTestResultDto, EventLogDto, InvalidateQcTestResultDto, QualityControlData, QualityControlAssignmentDto, QcTestResultDto } from '@services/dtos'
import { preparationLotService } from '@services/PreparationLotService'
import { generatorElutionService } from '@services/generatorElutionService'
import { qcTestResultService } from '@services/QcTestResultService'

type EntityRef = { entityType: 'preparationLot' | 'generatorElution'; entityId: string }

const fetchQualityControls = ({ entityType, entityId }: EntityRef): Promise<QualityControlData | null> =>
  entityType === 'preparationLot'
    ? preparationLotService.getQualityControls(entityId)
    : generatorElutionService.getQualityControls(entityId)

interface OperationQualityControlsState {
  assignment: QualityControlAssignmentDto | null
  testResults: QcTestResultDto[]
  canComplete: boolean
  completionLog: EventLogDto | null
  loading: boolean
  submitting: boolean
}

const initialState: OperationQualityControlsState = {
  assignment: null,
  testResults: [],
  canComplete: false,
  completionLog: null,
  loading: false,
  submitting: false,
}

export const getPreparationLotQualityControls = createAsyncThunk<QualityControlData | null, string>(
  'operationQualityControls/fetchPreparationLot',
  (id) => preparationLotService.getQualityControls(id)
)

export const getGeneratorElutionQualityControls = createAsyncThunk<QualityControlData | null, string>(
  'operationQualityControls/fetchGeneratorElution',
  (id) => generatorElutionService.getQualityControls(id)
)

export const savePreparationLotQcTestResult = createAsyncThunk<
  QualityControlData | null,
  { entityId: string; dto: CreateQcTestResultDto; file?: File }
>('operationQualityControls/savePreparationLot', async ({ entityId, dto, file }) => {
  await preparationLotService.recordQcTestResult(entityId, dto, file)
  return fetchQualityControls({ entityType: 'preparationLot', entityId })
})

export const saveGeneratorElutionQcTestResult = createAsyncThunk<
  QualityControlData | null,
  { entityId: string; dto: CreateQcTestResultDto; file?: File }
>('operationQualityControls/saveGeneratorElution', async ({ entityId, dto, file }) => {
  await generatorElutionService.recordQcTestResult(entityId, dto, file)
  return fetchQualityControls({ entityType: 'generatorElution', entityId })
})

export const invalidateQcTestResult = createAsyncThunk<
  QualityControlData | null,
  { resultId: string; dto: InvalidateQcTestResultDto } & EntityRef
>('operationQualityControls/invalidate', async ({ resultId, dto, entityType, entityId }) => {
  await qcTestResultService.invalidate(resultId, dto)
  return fetchQualityControls({ entityType, entityId })
})

export const completePreparationLotQualityControl = createAsyncThunk<
  QualityControlData | null,
  { entityId: string; completedByMemberId: number }
>('operationQualityControls/completePreparationLot', async ({ entityId, completedByMemberId }) => {
  await preparationLotService.completeQualityControl(entityId, completedByMemberId)
  return fetchQualityControls({ entityType: 'preparationLot', entityId })
})

export const completeGeneratorElutionQualityControl = createAsyncThunk<
  QualityControlData | null,
  { entityId: string; completedByMemberId: number }
>('operationQualityControls/completeGeneratorElution', async ({ entityId, completedByMemberId }) => {
  await generatorElutionService.completeQualityControl(entityId, completedByMemberId)
  return fetchQualityControls({ entityType: 'generatorElution', entityId })
})

const operationQualityControlsSlice = createSlice({
  name: 'operationQualityControls',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    const onPending = (state: OperationQualityControlsState) => { state.loading = true }
    const onFulfilled = (state: OperationQualityControlsState, action: { payload: QualityControlData | null }) => {
      state.assignment = action.payload?.assignment ?? null
      state.testResults = action.payload?.testResults ?? []
      state.canComplete = action.payload?.canComplete ?? false
      state.completionLog = action.payload?.completionLog ?? null
      state.loading = false
    }
    const onRejected = (state: OperationQualityControlsState) => { state.loading = false }
    const onSubmitPending = (state: OperationQualityControlsState) => { state.submitting = true }
    const onSubmitFulfilled = (state: OperationQualityControlsState, action: { payload: QualityControlData | null }) => {
      state.assignment = action.payload?.assignment ?? null
      state.testResults = action.payload?.testResults ?? []
      state.canComplete = action.payload?.canComplete ?? false
      state.completionLog = action.payload?.completionLog ?? null
      state.submitting = false
    }
    const onSubmitRejected = (state: OperationQualityControlsState) => { state.submitting = false }

    const onCompletePending = (state: OperationQualityControlsState) => { state.submitting = true }
    const onCompleteFulfilled = (state: OperationQualityControlsState, action: { payload: QualityControlData | null }) => {
      state.assignment = action.payload?.assignment ?? null
      state.testResults = action.payload?.testResults ?? []
      state.canComplete = action.payload?.canComplete ?? false
      state.completionLog = action.payload?.completionLog ?? null
      state.submitting = false
    }
    const onCompleteRejected = (state: OperationQualityControlsState) => { state.submitting = false }

    builder
      .addCase(completePreparationLotQualityControl.pending, onCompletePending)
      .addCase(completePreparationLotQualityControl.fulfilled, onCompleteFulfilled)
      .addCase(completePreparationLotQualityControl.rejected, onCompleteRejected)
      .addCase(completeGeneratorElutionQualityControl.pending, onCompletePending)
      .addCase(completeGeneratorElutionQualityControl.fulfilled, onCompleteFulfilled)
      .addCase(completeGeneratorElutionQualityControl.rejected, onCompleteRejected)
      .addCase(getPreparationLotQualityControls.pending, onPending)
      .addCase(getPreparationLotQualityControls.fulfilled, onFulfilled)
      .addCase(getPreparationLotQualityControls.rejected, onRejected)
      .addCase(getGeneratorElutionQualityControls.pending, onPending)
      .addCase(getGeneratorElutionQualityControls.fulfilled, onFulfilled)
      .addCase(getGeneratorElutionQualityControls.rejected, onRejected)
      .addCase(savePreparationLotQcTestResult.pending, onSubmitPending)
      .addCase(savePreparationLotQcTestResult.fulfilled, onSubmitFulfilled)
      .addCase(savePreparationLotQcTestResult.rejected, onSubmitRejected)
      .addCase(saveGeneratorElutionQcTestResult.pending, onSubmitPending)
      .addCase(saveGeneratorElutionQcTestResult.fulfilled, onSubmitFulfilled)
      .addCase(saveGeneratorElutionQcTestResult.rejected, onSubmitRejected)
      .addCase(invalidateQcTestResult.pending, onSubmitPending)
      .addCase(invalidateQcTestResult.fulfilled, onSubmitFulfilled)
      .addCase(invalidateQcTestResult.rejected, onSubmitRejected)
  },
})

export default operationQualityControlsSlice.reducer
